export const CONTACT_CATEGORIES = <const>[
  'Conflict of Interest',
  'Ethics / Solicitation',
  'Room Eligibility',
  'Room Safety Issue',
  'Voter / Nominator Eligibility',
  'Account / Sign-in',
  'Technical Issue',
  'Website / Public Data',
  'Feedback / Suggestion',
  'Other',
];

export type ContactCategory = typeof CONTACT_CATEGORIES[number];

export type ContactSubjectFieldMode = 'hidden' | 'optional' | 'required';

export interface ContactSubjectFields {
  room: ContactSubjectFieldMode;
  company: ContactSubjectFieldMode;
  person: ContactSubjectFieldMode;
}

export interface ContactRequest {
  anonymous?: boolean;
  name?: string;
  email?: string;
  category: ContactCategory;
  room?: string;
  company?: string;
  person?: string;
  description: string;
  source?: 'www' | 'portal';
}

export const CONTACT_MAX_NAME_LENGTH = 200;
export const CONTACT_MAX_EMAIL_LENGTH = 320;
export const CONTACT_MAX_SUBJECT_FIELD_LENGTH = 300;
export const CONTACT_MAX_DESCRIPTION_LENGTH = 10000;

const HIDDEN_SUBJECT_FIELDS: ContactSubjectFields = { room: 'hidden', company: 'hidden', person: 'hidden' };

export const CONTACT_CATEGORY_DESCRIPTIONS: Record<ContactCategory, string> = {
  'Conflict of Interest': 'A contributor may not have disclosed an affiliation, or you need guidance on how to treat your own.',
  'Ethics / Solicitation': 'A contributor used TERPECA status to request discounts or perks, or a business solicited nominations or favorable rankings.',
  'Room Eligibility': 'A nominated or awarded room may not meet the open-to-the-public or English-availability rules.',
  'Room Safety Issue': 'A room may have unsafe conditions or practices.',
  'Voter / Nominator Eligibility': 'A contributor may not meet the experience requirements, or you have questions about your own eligibility.',
  'Account / Sign-in': 'You can\'t get into an existing account, or you need to change how you sign in.',
  'Technical Issue': 'Something in the voter portal isn\'t working as expected.',
  'Website / Public Data': 'Something on the public TERPECA website looks wrong, such as a bio, room listing, or award logo.',
  'Feedback / Suggestion': 'An idea for how we can improve the project.',
  'Other': 'Anything else you think we should know.',
};

export const CONTACT_CATEGORY_SLUGS: Record<ContactCategory, string> = {
  'Conflict of Interest': 'conflict-of-interest',
  'Ethics / Solicitation': 'ethics-solicitation',
  'Room Eligibility': 'room-eligibility',
  'Room Safety Issue': 'room-safety-issue',
  'Voter / Nominator Eligibility': 'voter-nominator-eligibility',
  'Account / Sign-in': 'account-sign-in',
  'Technical Issue': 'technical-issue',
  'Website / Public Data': 'website-public-data',
  'Feedback / Suggestion': 'feedback-suggestion',
  'Other': 'other',
};

export function isContactCategory(value: unknown): value is ContactCategory {
  return typeof value === 'string' && (<readonly string[]>CONTACT_CATEGORIES).includes(value);
}

export function contactCategoryFromParam(value: unknown): ContactCategory | '' {
  if (typeof value !== 'string' || !value) {
    return '';
  }
  if (isContactCategory(value)) {
    return value;
  }
  const match = CONTACT_CATEGORIES.find(category => CONTACT_CATEGORY_SLUGS[category] === value);
  return match || '';
}

export function getContactSubjectFields(category: unknown): ContactSubjectFields {
  if (!isContactCategory(category)) {
    return HIDDEN_SUBJECT_FIELDS;
  }
  switch (category) {
    case 'Conflict of Interest':
      return { room: 'optional', company: 'optional', person: 'optional' };
    case 'Ethics / Solicitation':
      return { room: 'optional', company: 'optional', person: 'optional' };
    case 'Room Eligibility':
      return { room: 'required', company: 'optional', person: 'hidden' };
    case 'Room Safety Issue':
      return { room: 'required', company: 'optional', person: 'hidden' };
    case 'Voter / Nominator Eligibility':
      return { room: 'hidden', company: 'hidden', person: 'required' };
    case 'Website / Public Data':
      return { room: 'optional', company: 'optional', person: 'optional' };
    default:
      return HIDDEN_SUBJECT_FIELDS;
  }
}

export function buildContactEmailSubject(projectTitle: string, category: ContactCategory,
                                         room: string, company: string, person: string): string {
  const involved = [room, company, person]
    .map(value => truncateForSubject(value))
    .filter(value => value);
  const suffix = involved.length ? `: ${involved.join(' / ')}` : '';
  return `[${projectTitle} Contact] ${category}${suffix}`;
}

function truncateForSubject(value: string): string {
  const trimmed = (value || '').trim().replace(/\s+/g, ' ');
  if (trimmed.length <= 80) {
    return trimmed;
  }
  return trimmed.slice(0, 77).trimEnd() + '...';
}
